import { useMemo } from 'react';
import { MediaType } from '@brightmls/nextgen.lae.mediacenter';
import { useTenant } from '../../../../contexts/TenantContext';
import { useFeatureFlags } from '../../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../../flags';
import type { MediaV2Config } from './types';

interface MediaLimits {
  photos: {
    maxCount: number;
    fileConstraints: {
      maxSizeBytes: number;
      minSizeBytes: number;
      allowedFormats: string[];
      minWidth: number;
      minHeight: number;
      recommendedWidth: number;
      recommendedHeight: number;
      maxFileNameLength: number;
      maxResizeWidth: number;
      maxResizeHeight: number;
      jpegQuality: number;
    };
  };
  documents: {
    maxCount: number;
    fileConstraints: {
      maxSizeBytes: number;
      minSizeBytes: number;
      allowedFormats: string[];
      maxFileNameLength: number;
    };
  };
  tours: {
    maxCount: number;
    fileConstraints: {
      maxSizeBytes: number;
      allowedFormats: string[];
      maxFileNameLength: number;
    };
  };
}

interface MediaConstraintsResult {
  getEnabledTypes: () => MediaType[];
  getCategoryMapping: () => {
    photos: string[];
    documents: string[];
    tours: string[];
  };
  limits: MediaLimits;
}

/**
 * Hook that provides media constraints with context awareness
 * Can access isResoSystem, feature flags, and other context values
 */
export function useMediaConstraints(): MediaConstraintsResult {
  const { supportsClientResoWebApi } = useTenant();
  const { getFeatureValue } = useFeatureFlags();

  const isResoSystem = supportsClientResoWebApi();
  const mediaV2Config = getFeatureValue(FeatureFlags.NGW_MEDIA_V2) as
    | MediaV2Config
    | undefined;

  return useMemo(
    () => ({
      getEnabledTypes: (): MediaType[] => {
        const types: MediaType[] = [MediaType.Photo, MediaType.Document];
        if (isResoSystem) {
          types.push(MediaType.Tour);
        }
        return types;
      },

      getCategoryMapping: () => ({
        photos: ['Photo'],
        documents: isResoSystem ? ['Document', 'Floor Plan'] : ['Document'],
        tours: isResoSystem
          ? ['Unbranded Virtual Tour', 'Branded Virtual Tour']
          : [],
      }),

      limits: {
        photos: {
          // Use feature flag limit if available, otherwise fall back to default
          maxCount: mediaV2Config?.limits?.photos?.maxCount ?? 50,
          fileConstraints: {
            maxSizeBytes: 20 * 1024 * 1024, // 20MB
            minSizeBytes: 1024, // 1KB
            allowedFormats: ['jpg', 'jpeg'],
            minWidth: 10,
            minHeight: 10,
            recommendedWidth: 1920,
            recommendedHeight: 1080,
            maxFileNameLength: 50,
            maxResizeWidth: 2048,
            maxResizeHeight: 1536,
            jpegQuality: 0.85,
          },
        },
        documents: {
          // Use feature flag limit if available, otherwise fall back to default
          maxCount: mediaV2Config?.limits?.documents?.maxCount ?? 20,
          fileConstraints: {
            maxSizeBytes: 20 * 1024 * 1024,
            minSizeBytes: 1 * 1024,
            allowedFormats: ['pdf'],
            maxFileNameLength: 50,
          },
        },
        tours: {
          // Use feature flag limit if available, otherwise fall back to default
          maxCount: mediaV2Config?.limits?.tours?.maxCount ?? 20,
          fileConstraints: {
            maxSizeBytes: 20 * 1024 * 1024,
            allowedFormats: ['html', 'zip'],
            maxFileNameLength: 50,
          },
        },
      },
    }),
    [isResoSystem, mediaV2Config],
  );
}
